
/**
 * TokenManager (uses window.globalVariables)
 * 
 */
class TokenManager {
  constructor({
    initialToken,
    refreshUrl,
    bufferSeconds = 60,
    reloadOnRefresh = true,
    maxRetries = 3,
  }) {
    this.token = initialToken || null;
    this.refreshUrl = refreshUrl;
    this.bufferSeconds = bufferSeconds;
    this.reloadOnRefresh = reloadOnRefresh;
    this.maxRetries = maxRetries;
    this._refreshPromise = null;
  }

  async getValidToken(customerId) {
    if (!this._isExpiredOrExpiring()) {
      return this.token;
    }
    await this._refresh(customerId);
    return this.token;
  }

  _isExpiredOrExpiring() {
    const payload = this._decodeJwt(this.token);
    if (!payload || !payload.exp) return true;

    let expSeconds = payload.exp;
    // handle ms-exp too
    if (expSeconds > 1e12) {
      expSeconds = Math.floor(expSeconds / 1000);
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const refreshThreshold = expSeconds - this.bufferSeconds;

    return nowSeconds >= refreshThreshold;
  }

  _decodeJwt(token) {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const json = atob(padded);
      return JSON.parse(json);
    } catch (err) {
      console.warn('[token-manager] Failed to decode JWT:', err);
      return null;
    }
  }

  async _refresh(customerId) {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = this._doRefresh(customerId)
      .catch(err => {
        console.error('[token-manager] refresh failed:', err);
        throw err;
      })
      .finally(() => {
        this._refreshPromise = null;
      });

    return this._refreshPromise;
  }

  async _doRefresh(customerId) {
    if (!customerId) {
      throw new Error('Cannot refresh token: missing customer id');
    }

    let lastError = null;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        const res = await fetch(this.refreshUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: customerId }),
        });

        if (!res.ok) {
          throw new Error(`Refresh failed: ${res.status} ${res.statusText}`);
        }

        // best case: backend gives us the new token
        let data = null;
        try {
          data = await res.json();
        } catch (_) {}

        if (data && data.token) {
          this.token = data.token;
          console.info('[token-manager] token refreshed in memory');
          return;
        }

        // fallback: backend updated metafield, we need reload
        if (this.reloadOnRefresh) {
          console.info('[token-manager] refreshed on backend, reloading…');
          location.reload();
        }

        return;
      } catch (err) {
        lastError = err;
        console.error(`[token-manager] refresh attempt ${attempt} failed`, err);

        if (attempt < this.maxRetries) {
          await new Promise(r => setTimeout(r, attempt * 500));
        }
      }
    }

    throw lastError || new Error('All refresh attempts failed');
  }
}

// internal name – can’t collide with window.authFetch
async function _authFetchInternal(url, options = {}, tokenManager, customerId) {
  const token = await tokenManager.getValidToken(customerId);

  const headers = new Headers(options.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);

  return fetch(url, { ...options, headers });
}

(function initTokenManager() {
  const gv = window.globalVariables || {};
  const customerId = gv.customerId;
  const hasCustomer = !!customerId;
  const hasToken = !!gv.customer_token;

  if (!hasCustomer) {
    window.authFetch = (url, options = {}) => fetch(url, options);
    return;
  }

  // If customer exists but has no token, generate one via the refresh endpoint
  if (!hasToken) {
    window.authFetch = (url, options = {}) => fetch(url, options);
    fetch(`${gv.api_url}/customers/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: customerId }),
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data && data.token) {
          console.info('[token-manager] Generated initial token, reloading...');
          location.reload();
        }
      })
      .catch((err) => {
        console.error('[token-manager] Failed to generate initial token:', err);
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(err, { tags: { feature: 'token-generation' } });
        }
      });
    return;
  }

  const tokenManager = new TokenManager({
    initialToken: gv.customer_token,
    refreshUrl: `${gv.api_url}/customers/refresh`,
    bufferSeconds: 60,
    reloadOnRefresh: true,
    maxRetries: 3,
  });

  window.tokenManager = tokenManager;

  window.authFetch = (url, options = {}) =>
    _authFetchInternal(url, options, tokenManager, customerId);
})();


let productSlider = null;
let quickShopSlider = null;

/**
* Dropdown component Events and Methods
*
* @param {element} element - Dropdown element
*/
class Dropdown {
  constructor(element) {
    this.element = element;
    this.dropdownParent = element.parentElement;
    this.supportedEvents = ["mouseover", "focusin", "click", "mouseout", "touchstart"];
    this.init();
  }
  
  init() {
    this.supportedEvents.forEach((type) => {
      if (type === 'click') {
        this.element.addEventListener(type, (evt) => {
          evt.stopPropagation();
          this.handleDropdownEvent(evt);
        });
      } else if (type === 'mouseout' || type === 'touchstart') {
        this.dropdownParent.addEventListener(type, (evt) => {
          let mobileContainer = evt.currentTarget.closest('[data-touch-moveable]');
          if (!mobileContainer) {
            this.dropdownHide(this.dropdownParent, evt.type);
          }
        }, true);
      } else if (type === 'mouseover' || type === 'focusin') {
        this.dropdownParent.addEventListener(type, (evt) => {
          let mobileContainer = evt.currentTarget.closest('[data-touch-moveable]');
          if (!mobileContainer) {
            this.dropdownShow(this.dropdownParent);
          }
        });
        this.element.addEventListener(type, (_evt) => {
          this.childOnFocusChange(this.dropdownParent);
        });
      }
    });
    
    this.bindEvents();
  }
  
  bindEvents(){
    let closeBtn = this.dropdownParent.querySelector('.close-submenu');
    if (closeBtn) {
      closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        let parentElement = evt.currentTarget.closest('.dropdown');
        if (parentElement) this.dropdownToggle(parentElement);
      });
    }
  }
  
  handleDropdownEvent(evt) {
    /* let currentTarget = evt.currentTarget;
    let liveTarget = evt.target;
    if (liveTarget.classList.contains('nav-link-title')) {
    let linkHref = currentTarget.getAttribute('data-href');
    if (linkHref) window.location.href = linkHref;
    } else {*/
    this.dropdownToggle(this.dropdownParent);
    /* } */
  }
  
  dropdownToggle(dropdownParent) {
    setTimeout(() => {
      dropdownParent.classList.contains('open')
      ? this.dropdownHide(dropdownParent)
      : this.dropdownShow(dropdownParent);
    }, 50);
  }
  
  dropdownShow(dropdownParent) {
    if (!dropdownParent) {
      return;
    }
    
    this.element.setAttribute('aria-expanded', true);
    if (!dropdownParent.closest('[data-role="drawer"]')) {
      dropdownParent.classList.add('open');
    } else {
      Utility.toggleElement(dropdownParent, 'open');
    }
  }
  
  dropdownHide(dropdownParent, eventType) {
    if (!dropdownParent) {
      return;
    }
    
    this.element.setAttribute('aria-expanded', false);
    if (!dropdownParent.closest('[data-role="drawer"]')) {
      dropdownParent.classList.remove('open');
    } else {
      Utility.toggleElement(dropdownParent, 'close');
    }
    
    if (eventType === 'mouseout' && dropdownParent.parentElement.closest('.dropdown')) {
      dropdownParent.parentElement.closest('.dropdown').classList.remove('open');
    }
    
    let childDropdowns = dropdownParent.querySelectorAll('.dropdown.open');
    if (childDropdowns.length > 0) {
      childDropdowns.forEach((child) => {
        child.classList.remove('open');
        if (child.querySelector('a[data-toggle="open"]'))
          child.querySelector('a[data-toggle="open"]').setAttribute('aria-expanded', false);
      });
    }
  }
  
  childOnFocusChange(dropdownParent) {
    let openDropdowns = Array.from(dropdownParent.parentElement.querySelectorAll('.dropdown.open'));
    let currentIndex = openDropdowns.indexOf(dropdownParent);
    openDropdowns.forEach((ele) => {
      let eleIndex = openDropdowns.indexOf(ele);
      if (eleIndex !== currentIndex) {
        this.dropdownHide(ele);
      }
    });
  }
}
const allDropdowns = document.querySelectorAll('a[is="drop-down"]');
allDropdowns.forEach((ele) => {
  new Dropdown(ele);
});

/**
* Close SubMenu Dropdowns on Regular Nav Item fous or click
*
* @param {element} element - Dropdown element
*/
class DefaultNavItem {
  constructor(element) {
    this.element = element;
    this.supportedEvents = ["mouseover", "focusin", "click", "mouseout", "touchstart"];
    this.init();
  }
  
  init() {
    let _this = this.element;
    this.supportedEvents.forEach((type) => {
      _this.addEventListener(type, (e) => {
        e.stopPropagation();
        let currentTarget = e.currentTarget;
        if (!currentTarget.closest('[data-touch-moveable]')) {
          this.hideDropdown(currentTarget.parentElement);
        }
      });
    });
    
    _this.addEventListener('touchend', (e) => {
      e.stopPropagation();
    });
  }
  
  hideDropdown(dropdownParent) {
    let openDropdowns = Array.from(dropdownParent.parentElement.querySelectorAll('.dropdown.open'));
    openDropdowns.forEach((ele) => {
      ele.classList.remove('open');
      ele.querySelector('[is="drop-down"]').setAttribute('aria-expanded', false);
    });
  }
}
const allDefaultNav = document.querySelectorAll('a[is="simple-menu-item"]');
allDefaultNav.forEach((ele) => {
  new DefaultNavItem(ele);
});

/**
* background Overlay
*/
class siteOverlay extends HTMLElement{
  constructor() {
    super();
    
    this.menuDrawer = document.querySelector('#mobile-menu-drawer');
    this.cartDrawer = document.querySelector('#ajax-cart');
    this.searchDrawer = document.querySelector('search-modal');
    
    this.bindEvents();
  }
  
  connectedCallback() {
    
  }
  
  /**
  * bind Click and Touchend event to this custom component
  */
  bindEvents(){
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('touchend', this.onClick.bind(this));
  }
  
  /**
  * Click event of Site overlay
  * @param {event} Event instance performed
  */
  onClick(event){
    const closeCart = this.cartDrawer && this.cartDrawer.classList.contains("opened-drawer");
    const closeMenu = this.menuDrawer && this.menuDrawer.classList.contains("opened-drawer");
    const closeSearch = this.searchDrawer && this.searchDrawer.classList.contains("open__modal");
    
    if(closeCart) document.querySelector('.close-ajax--cart').dispatchEvent(new Event('click'));
    if(closeMenu) document.querySelector('.close-mobile--navbar').dispatchEvent(new Event('click'));
    if(closeSearch) document.querySelector('.search-modal__close-button').dispatchEvent(new Event('click'));
    
    this.hideOverlay();
  }
  
  /**
  * Show Overlay
  */
  showOverlay(){
    document.querySelector('#site-overlay').classList.add('overlay--body');
    document.body.classList.add('scroll-fixed');
  }
  
  /**
  * Hide overlay 
  */
  hideOverlay(){
    document.querySelector('#site-overlay').classList.remove('overlay--body');
    document.body.classList.remove('scroll-fixed');
    document.body.classList.remove('mobile-nav-open');
  }
}
customElements.define("site-overlay", siteOverlay, {
  once: true
});

/**
* Slider Component
*
*/
class sliderElement extends HTMLElement{
  constructor() {
    super();
    this.sliderEle = this.querySelector('.swiper-slider');
    this.sliderOptions = JSON.parse(this.sliderEle.getAttribute('data-slider'));
  }
  
  static get observedAttributes() {
    return ["data-slider"];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-slider" && newValue) {
      this.sliderOptions = JSON.parse(newValue);
    }
  }
  
  connectedCallback() {
    if(!this.sliderEle) return;
    
    let thumbSlider = null;
    
    // Set up nav slider 
    if(this.sliderEle.hasAttribute('data-nav')){
      thumbSlider = document.querySelector(`.${this.sliderEle.getAttribute('data-nav')}`);
      if(!thumbSlider){
        return;
      }
      
      const thumbSliderOptions = JSON.parse(thumbSlider.getAttribute('data-slider'));
      thumbSlider = new Swiper(thumbSlider, thumbSliderOptions);
      
      this.sliderOptions.thumbs = {
        swiper: thumbSlider
      }
    }
    
    const sliderInstance = new Swiper(this.sliderEle, this.sliderOptions);
    
    if(this.sliderEle.classList.contains('product-carousel')){
      productSlider = sliderInstance;
    }else if(this.sliderEle.classList.contains('quickshop-slider')){
      quickShopSlider = sliderInstance;
    }
    
    this.slideChangeEvt(sliderInstance, thumbSlider);
  }
  
  slideChangeEvt(slider, thumbSlider){   
    slider.on('slideChange', (event) => {
      if(thumbSlider) thumbSlider.slideTo(slider.activeIndex);
      
      const activeVideos = slider.el.querySelectorAll(
        ".custom-video-container.playing--video"
      );
      
      if(activeVideos.length > 0){        
        activeVideos.forEach((container)=>{
          let video = container.querySelector('custom-video');
          let videoType = video.getAttribute('data-video-type');
          let videoEle = video.querySelector('video') || video.querySelector('iframe');
          if(videoType == 'local'){
            videoEle.pause();
          }else if(videoType == 'youtube'){
            videoEle.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        });
      }
    });
  }
}
let lookForSwiper = setInterval(() => {
  if(typeof Swiper == 'function'){
    customElements.define('slider-element', sliderElement);
    clearInterval(lookForSwiper);
  } 
}, 100);

/**
* Custom Video
*/
class customVideo extends HTMLElement {
  constructor() {
    super();
    
    this.type = this.getAttribute('data-video-type');
    this.videoURL = this.getAttribute('data-video-id');
    this.placementType = this.getAttribute('data-placement');
    this.videoStatus = 'empty';
    
    this.container = this.closest('.custom-video-container');
    this.videoModal = document.getElementById('PopupModal-video');
    this.contentSection = this.querySelector('.content-section');
    this.videoSection = this.querySelector('.video-section');
    if(this.placementType == 'popup') this.videoSection = this.videoModal.querySelector('.video-container');
    this.triggerVideo = this.container.querySelector('[data-trigger-video]');
    
    this.bindEvents();
  }
  
  connectedCallback() {
    // Connected callback
  }
  
  /**
  * Binding events on the video button
  */
  bindEvents(){
    if(this.querySelector('[data-postervideo]')) this.managePosterVideos();
    if(this.triggerVideo) this.triggerVideo.addEventListener('click', this.toggleView.bind(this));
    // if(this.placementType == 'grid') this.addEventListener('focusout', this.focusOut.bind(this));
  }
  
  /**
  * Focus Out Event to close drawer
  *
  * @param {event} Event instance
  */
  focusOut(_event){
    if(this.container.classList.contains('playing--video')) this.toggleView();
  }
  
  /**
  * Toggle of video modal
  */
  toggleView(_event){
    let isOpen = this.container.classList.contains('playing--video');
    if(isOpen){
      this.container.classList.remove('playing--video');
    }else{
      if(this.videoStatus == 'empty' || this.placementType == 'popup'){
        const videoElement = this._buildVideo();
        videoElement.id = 'custom-video' + Math.floor((Math.random() * 100) + 1);;
        this.videoSection.appendChild(videoElement);
        this.videoStatus = 'loaded';
        
        setTimeout(() => {
          (this.placementType == 'popup') ? openModal(this.videoModal, this.triggerVideo) : this.container.classList.add('playing--video');
          this.pauseOnVisibilityChange(videoElement);
          this._playVideo(videoElement);
        }, 700);        
      }else{
        const videoElement = this.querySelector('.video-section video') || this.querySelector('.video-section iframe');
        this.container.classList.add('playing--video');
        this._playVideo(videoElement);
      }
    }
  }
  
  _playVideo(video){
    if(this.type == 'local'){
      video.play();
    }else if(this.type == 'youtube'){
      video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    }
  }
  
  /**
  * 
  * Create the video html element
  */
  _buildVideo(){
    let video;
    
    if(this.type == 'local'){
      video = document.createElement('video');
      video.src = this.videoURL;
      if(this.dataset.controls == 'true') video.controls = this.dataset.controls;
      if(this.dataset.muted == 'true') video.muted = this.dataset.muted;
      if(this.dataset.autoplay == 'true') video.autoplay = this.dataset.autoplay;
      if(this.dataset.loop == 'true') video.loop = this.dataset.loop;
      video.playsInline = true;
    }else if(this.type == 'youtube'){
      let videoID = this.videoURL.replace('https://www.youtube.com/watch?v=', '');
      video = document.createElement( "iframe" );
      video.setAttribute( "allowfullscreen", "" );
      video.setAttribute( "frameborder", "0" );
      video.setAttribute( "allow", "autoplay");
      video.setAttribute( "src", "https://www.youtube.com/embed/"+ videoID +`?rel=0&showinfo=0&autoplay=${this.dataset.autoplay}&muted=${this.dataset.muted}&controls=${this.dataset.controls}&loop=${this.dataset.loop}&playlist=${videoID}&enablejsapi=1&origin=${window.origin}` );
    }
    return video;
  }
  
  /**
  * Pause video on scroll when out of viewport
  *
  */
  pauseOnVisibilityChange(videoEle){
    let scrollObserver = new IntersectionObserver((entries) => { 
      if((this.type == 'local' && videoEle == undefined) || (this.type == 'youtube' && videoEle.contentWindow == null)){
        scrollObserver.disconnect();
        return;
      }
      
      entries.forEach(entry => {
        if(entry.intersectionRatio == 0){
          if(this.type == 'local'){
            videoEle.pause();
          }else if(this.type == 'youtube'){
            videoEle.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        }
      });
    });
    scrollObserver.observe(videoEle);
  }
  
  /**
  * Poster Video Autoplay
  *
  */
  managePosterVideos(){
    let posterVideo = this.querySelector('[data-postervideo]');
    if(!posterVideo || posterVideo == undefined) return;
    
    const videoIntersection = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target) {
          let sourceTag = document.createElement('source');
          sourceTag.src = entry.target.dataset.src;
          sourceTag.type = "video/mp4";
          entry.target.appendChild(sourceTag)
          entry.target.play();
          observerPosterVideo.disconnect();         
        }
      }
    };
    const observerPosterVideo = new IntersectionObserver(videoIntersection);
    observerPosterVideo.observe(posterVideo);
  }
}
customElements.define("custom-video", customVideo);

/**
* Open Modal javscript trigger
*
* */
function openModal(modal, opener){
  if(modal && opener != undefined) modal.show(opener);
}

/**
* Modal Dialogue
*
*/
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    
    this.modal = this.querySelector('.modal');
    this.modalOpeners = [...document.querySelectorAll(`[data-modal-opener="${this.id}"]`)];
    this.closeBtn = this.querySelector('[id^="ModalClose-"]');
  }
  
  connectedCallback() {
    this.modalOpeners.forEach((opener)=>{
      opener.addEventListener('click', (evt)=>{
        evt.preventDefault();
        this.show(opener);
      });
    });
    
    if(this.closeBtn){
      this.closeBtn.addEventListener(
        'click',
        this.hide.bind(this)
      );
    }
    
    this.addEventListener('click', (evt) => {
      if (evt.target.nodeName === 'MODAL-DIALOG') this.hide();
    });
    
    this.addEventListener('keyup', (evt) => {
      if (evt.code && evt.code.toUpperCase() === 'ESCAPE') this.hide();
    });
  }
  
  /**
  * Open the Modal
  * @param {element} opener 
  */
  show(opener) {
    this.openedBy = opener;
    this.modal.classList.add('open');
    siteOverlay.prototype.showOverlay();
    Utility.trapFocus(this);
    Utility.forceFocus(this.closeBtn);
  }
  
  /**
  * Close Modal Dialogue
  */
  hide() {
    this.modal.classList.remove('open');
    siteOverlay.prototype.hideOverlay();
    Utility.removeTrapFocus(this.openedBy);
    
    if(this.id == 'PopupModal-video'){
      this.querySelector('.video-container').innerHTML = '';
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
* Manage Custom Notification Trigger
*
*/
class Notification extends HTMLElement {
  constructor() {
    super();
    
    this.container = this.querySelector('.snotifyToast');
    this.closeBtn = this.querySelector('.snotifyToast__close');
    this.animateNotifcation = null;
  }
  
  connectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hide();
      });
    }
  }
  
  /**
  * Notification Type
  * @param title 
  * @param body 
  * @param options 
  */
  updateNotification(title, body, options){
    this.querySelector('.snotifyToast__title').textContent = title;
    this.querySelector('.snotifyToast__body').textContent = body;
    
    this.container.classList.add('open', `snotify-${options.type}`);
    this._manageToggle(options.timeout);
  }
  
  /**
  * Duration of notification to hide again
  * @param timeout 
  */
  _manageToggle(timeout) {
    this.progressBar = this.querySelector('[data-toast-pr]');
    const totalFrames = Math.ceil(timeout / 16.67) / 2;
    let frame =0; 
    let width = 0;
    
    this.animateNotifcation = () => {
      width = (frame / totalFrames) * 100;
      this.progressBar.setAttribute('style', `width: ${width}%`);
      if (frame < totalFrames) {
        frame++;
        requestAnimationFrame(this.animateNotifcation);
      } else {
        this.closeBtn.dispatchEvent(new Event('click'));
      }
    };
    requestAnimationFrame(this.animateNotifcation);
  }
  
  hide() {
    cancelAnimationFrame(this.animateNotifcation);
    this.container.className = 'snotifyToast';
    [this.querySelector('.snotifyToast__title'),this.querySelector('.snotifyToast__body')].forEach(ele => ele.textContent = '');
    this.querySelector("[data-toast-pr]").removeAttribute("style");
  }
}
customElements.define("toast-notification", Notification);
window.notificationEle = document.querySelector('toast-notification');
// notificationEle.updateNotification('Title', 'Body', {
//   type: 'success',
//   timeout: 5000
// });


document.addEventListener('DOMContentLoaded', (e) => {
  function isElementVisible(el) {
    if (!el) return false; // If element doesn't exist, return false
    
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0; // Checks if any part of the element is in view
  }
  
  function setMobileDrawerHeight() {
    const mobileHeader = document.querySelector('#mobile-menu-drawer');
    if (!mobileHeader) return;
    
    const stickyHeader = document.querySelector('[data-sticky-type]');
    const topBar = document.querySelector('[data-top-bar]');
    
    const headerHeight = isElementVisible(stickyHeader) ? (stickyHeader?.offsetHeight || 0) : 0;
    const topBarHeight = isElementVisible(topBar) ? (topBar?.offsetHeight || 0) : 0;
    
    const height = headerHeight + topBarHeight;
    mobileHeader.style.setProperty("--top-from-header", `${height}px`);
    console.log("Visible Header Height:", height);
  }
  
  // Run once on page load
  setMobileDrawerHeight();
  
  // Optimize scroll event with debounce
  function debounce(func, delay = 100) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }
  
  // Attach event listener with debounce
  window.addEventListener('scroll', debounce(setMobileDrawerHeight, 100));
  window.addEventListener('resize', debounce(setMobileDrawerHeight, 100)); // Update on resize too

  // Login event tracking
  const LOGIN_EVENT_INTERVAL = 10 * 60 * 1000; // 10 minutes in milliseconds
  const companyId = document.body.getAttribute('data-company-id') || null;

  // Helper function to retry company assignment API call
  const retryCompanyAssignment = async (maxRetries = 3) => {
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (typeof Sentry !== 'undefined') {
        Sentry.addBreadcrumb({
          category: 'company',
          message: `Company assignment attempt ${attempt}/${maxRetries}`,
          level: 'info',
        });
      }
      try {
        const response = await window.authFetch(
          `${window.globalVariables.api_url}/customer/companies/assign`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.ok) {
          return response; // Success, return the response
        } else {
          lastError = { response, attempt };
          console.log(`Company assignment attempt ${attempt} failed with status: ${response.status}`);
          
          // If this is not the last attempt, wait a bit before retrying
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Progressive delay
          }
        }
      } catch (error) {
        lastError = { error, attempt };
        console.log(`Company assignment attempt ${attempt} failed with error:`, error);
        
        // If this is not the last attempt, wait a bit before retrying
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Progressive delay
        }
      }
    }
    
    // All attempts failed, throw the last error
    if (lastError.response) {
      throw new Error(`Failed to assign company after ${maxRetries} attempts. Final status: ${lastError.response.status}`);
    } else {
      throw new Error(`Failed to assign company after ${maxRetries} attempts. Final error: ${lastError.error.message}`);
    }
  };

  const trackLoginEvent = async () => {
    if (typeof Sentry !== 'undefined') {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Login event triggered',
        level: 'info',
        data: { hasCompanyId: !!companyId },
      });
    }
    try {
      const response = await window.authFetch(
        `${window.globalVariables.api_url}/customer/events`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'LOGIN',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to track login event. Status: ${response.status}`);
      }

      if (typeof Sentry !== 'undefined') {
        Sentry.addBreadcrumb({ category: 'auth', message: 'Login event tracked successfully', level: 'info' });
      }

      // if (response.ok) {
        if(!companyId) {
          if (typeof Sentry !== 'undefined') {
            Sentry.addBreadcrumb({ category: 'company', message: 'No companyId — starting company assignment', level: 'info' });
          }
          try {
            const response2 = await retryCompanyAssignment(3);
            
            await new Promise(r => setTimeout(r, 2000));
            
            // Success - redirect to locations
            window.location.href = "https://shopify.com/70395527401/account/locations?return_to=/";
            
          } catch (assignmentError) {
            // All retry attempts failed
            document.querySelectorAll('.page-loader')?.forEach((ele)=>{
              ele.classList.remove('active');
            })

            console.error('Company assignment failed after login event (after 3 retries):', assignmentError);
            if (typeof Sentry !== 'undefined') {
              Sentry.captureException(assignmentError, { tags: { feature: 'company-assignment' } });
            }
            throw assignmentError;
          }
        }else{
          document.querySelectorAll('.page-loader')?.forEach((ele)=>{
            ele.classList.remove('active');
          })
        }
      // }

      // Update the last login event timestamp
      localStorage.setItem('lastLoginEvent', Date.now().toString());
    } catch (error) {
      console.error('Login event tracking failed:', error);
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, { tags: { feature: 'login-event' } });
      }
      document.querySelectorAll('.page-loader')?.forEach((ele)=>{
        ele.classList.remove('active');
      })
    }
  };

  const checkAndTrackLogin = () => {
    const lastLoginEvent = localStorage.getItem('lastLoginEvent');
    const currentTime = Date.now();

    // if (!lastLoginEvent || (currentTime - parseInt(lastLoginEvent)) >= LOGIN_EVENT_INTERVAL || !companyId) {
      trackLoginEvent();
    // }
  };

  // Check and track login on page load
  checkAndTrackLogin();
});

// ICEB-374: payment terms are no longer written onto the company location at
// checkout. A Shopify payment customization function (Icebox Payment Terms app)
// sets the buyer's role terms on the individual checkout instead, so there is
// nothing to apply here and nothing to revert afterwards.
try { localStorage.removeItem('roleBased'); } catch (e) { /* storage unavailable */ }

const setupCompanyNameInCartAttributes = async () => {
  const companyName = document.querySelector('[data-current-company-name]')?.getAttribute('data-current-company-name') || window.globalVariables.company_name;
  try {
    const body = JSON.stringify({
      attributes: {
        'Company Name': companyName
      }
    })
    await Utility.updateCart(body);
  } catch (error) {
    console.error('Cart company name update failed:', error);
    if (typeof Sentry !== 'undefined') {
      Sentry.captureException(error, { tags: { feature: 'cart-company-name' } });
    }
    notificationEle.updateNotification('Error in updating company name', error, {
      type: 'error',
      timeout: 5000
    });
  }
};

window.setupCheckoutButtons = () => {
  const checkoutButtons = document.querySelectorAll('[name="checkout"]');
  checkoutButtons.forEach(button => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();
      button.disabled = true;
      await setupCompanyNameInCartAttributes();
      window.location.href = "/checkout";
      button.disabled = false;
    });
  });
};

// If the buyer comes back from checkout with the back button and the browser
// restores this page from the bfcache, the click handler's disabled state is
// restored with it. Re-enable the buttons so checkout can be retried.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    document.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = false; });
  }
});

window.setupCheckoutButtons();

// Loop through all anchor tags with an href attribute
document.querySelectorAll('a[href]').forEach(link => {
  const href = link.getAttribute('href');
  const isExternal = href.startsWith('http') && !href.includes(location.hostname);
  if (isExternal) {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }
});

// Auto logout functionality
const standard_timeout = document.querySelector('[data-standard-timeout]').getAttribute('data-standard-timeout');
if (standard_timeout != 0) {
  const AUTO_LOGOUT_TIMEOUT = standard_timeout * 60 * 1000; // 30 minutes in milliseconds
  let inactivityTimer = null;

  // Function to check if user should be logged out based on timestamp
  const checkLogoutTimestamp = () => {
    const lastActivityTimestamp = localStorage.getItem('lastActivityTimestamp');
    if (lastActivityTimestamp) {
      const currentTime = Date.now();
      const timeDiff = currentTime - parseInt(lastActivityTimestamp);
      
      if (timeDiff >= AUTO_LOGOUT_TIMEOUT) {
        // If more than 30 minutes have passed, redirect to logout
        localStorage.removeItem('lastActivityTimestamp');
        window.location.href = '/account/logout';
        // console.log("Auto logout triggered");
      }
    }
  };

  // Function to update the last activity timestamp
  const updateLastActivityTimestamp = () => {
    localStorage.setItem('lastActivityTimestamp', Date.now().toString());
    resetInactivityTimer();
  };

  // Function to reset the inactivity timer
  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    
    inactivityTimer = setTimeout(() => {
      localStorage.removeItem('lastActivityTimestamp');
      window.location.href = '/account/logout';
      // console.log("Auto logout triggered");
    }, AUTO_LOGOUT_TIMEOUT);
  };

  // Function to initialize auto logout functionality
  const initAutoLogout = () => {
    // Check timestamp on page load
    checkLogoutTimestamp();
    
    // Update timestamp and reset timer on user activity
    const activityEvents = [
      'mousedown', 'mousemove', 'keypress',
      'scroll', 'touchstart', 'click'
    ];
    
    activityEvents.forEach(event => {
      document.addEventListener(event, updateLastActivityTimestamp);
    });
    
    // Initial timestamp update
    updateLastActivityTimestamp(); 
  };

  // document.addEventListener('DOMContentLoaded', initAutoLogout);

}

function initUpsellSlider() {
  var track = document.querySelector('[data-upsell-track]');
  if (!track) return;

  var slides = track.children;
  var prevBtn = document.querySelector('[data-upsell-prev]');
  var nextBtn = document.querySelector('[data-upsell-next]');
  var current = 0;

  function updateNav() {
    if (!slides.length) return;
    prevBtn.disabled = current === 0;
    nextBtn.disabled = current >= slides.length - 1;
  }

  function scrollToCurrent() {
    if (!slides.length) return;
    slides[current].scrollIntoView({ behavior: 'smooth', inline: 'start' });
    updateNav();
  }

  if (prevBtn && nextBtn && slides.length) {
    prevBtn.addEventListener('click', function() {
      if (current > 0) {
        current--;
        scrollToCurrent();
      }
    });

    nextBtn.addEventListener('click', function() {
      if (current < slides.length - 1) {
        current++;
        scrollToCurrent();
      }
    });

    track.addEventListener('scroll', function() {
      var closest = 0;
      var minDiff = Infinity;
      var trackRect = track.getBoundingClientRect();

      for (var i = 0; i < slides.length; i++) {
        var rect = slides[i].getBoundingClientRect();
        var diff = Math.abs(rect.left - trackRect.left);
        if (diff < minDiff) {
          minDiff = diff;
          closest = i;
        }
      }

      current = closest;
      updateNav();
    });

    updateNav();
  }
}

document.addEventListener('DOMContentLoaded', initUpsellSlider);


async function refreshPunchoutShipping() {
  const companyCheck = String(window.globalVariables?.company_name ?? "");
  console.log(companyCheck);

  const cart = await fetch('/cart.js').then(r => r.json());

  const perUnitShipping = (line) => {
    if (companyCheck === "TXRH &amp; Bubbas 33 Uniforms") return 0.00;
    return 5.00; // flat per-unit
  };

  const lines = cart.items.map((line) => {
    const shipping = perUnitShipping(line) * line.quantity;
    return {
      sku: line.sku,
      variant_id: line.variant_id,
      quantity: line.quantity,
      shipping: shipping.toFixed(2),
    };
  });

  const headerTotal = lines.reduce((sum, l) => sum + parseFloat(l.shipping), 0);

  window.punchoutShipping = {
    header_total: headerTotal.toFixed(2),
    lines,
  };
  console.log(window.punchoutShipping);
}

// run on load and on every cart change
document.addEventListener('DOMContentLoaded', refreshPunchoutShipping);
document.addEventListener('cart:updated', refreshPunchoutShipping); 
